import React from 'react';
import { connect } from 'react-redux';
import {
  CommunicationsDrawerInfo,
  CurrentAccountInfo,
} from '@amzn/aac-chrome-internal-api';
import { HMHConsistentIngressAnchor } from '@amzn/help-me-here-client';
import { ExternalSwitchWrapper, UtilityNavContainer } from './styles';
import EmptyAccountDropdown from '../AccountDropdown/EmptyAccountDropdown';
import AccountDropdown from '../AccountDropdown/AccountDropdown';
import { ConnectedWrappedAcesCommunicationsDrawer } from '../CommunicationsDrawer/CommunicationsDrawer';
import HelpDropdown from '../HelpDropdown/HelpDropdown';
import UserDropdown from '../UserDropdown/UserDropdown';
import SpoofingInfo from '../SpoofingInfo/SpoofingInfo';
import { PAGE_LAYOUT_TYPE, selectViewType, VIEW_TYPE } from 'src/app/slice';
import {
  selectCommunicationsDrawerInfo,
  selectHeaderState,
  selectHelpMenu,
  selectIsBackUpNav,
  selectIsSpoofing,
  selectSignOutInfo,
  SignOutInfo,
} from 'src/features/header/store/slice';
import {
  SelectedToolbarItem,
  selectSelectedToolbarItem,
} from 'src/features/nav/store/slice';
import { RootState } from 'src/app/store';
import { SignOutLink } from '../UserDropdown/SignOutLink';
import { ConnectedDynamicCommunicationsDrawer } from '../CommunicationsDrawer/DynamicCommunicationsDrawer';

import { AccountDropdownProvider } from '../AccountDropdown/AccountDropdownProvider';
import {
  SidenavBundleProvider,
  useHeaderBundle,
} from 'src/providers/PantherLocalizationProvider';
import { isStandardPageLayoutType } from 'src/commons/utils/helpers';
import { ExternalToggle } from '../ExternalToggle/ExternalToggle';
import SearchBar from '../SearchBar/SearchBar';

import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';
import { AdvertisingApiClientProvider } from 'src/providers/AdvertisingApiProvider';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import { useIsFeatureEnabled } from 'src/commons/hooks';
import { WeblabedFeatures } from 'src/commons/constants';
import { isBtswContext, isFromBtswWc, isFromTrim } from 'src/commons/utils';
import { questionCircleOutline } from '@amzn/storm-ui-icons';
import { ConnectedSalesAssistantButton } from '../SalesAssistant/SalesAssistantButton';
import { getTaktAttributes } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';
import UtilityButton from 'src/features/header/components/UtilityMenu/UtilityButton';

enum ACCOUNT_DROPDOWN_TYPE {
  EMPTY,
  STANDARD,
  NONE,
}

const renderCommunicationDrawer = (isMobile: boolean): React.ReactNode => {
  if (isMobile) {
    // renders a button to link to the notifications page for mobile
    return <ConnectedWrappedAcesCommunicationsDrawer />;
  } else {
    return (
      <AdvertisingApiClientProvider>
        <ConnectedDynamicCommunicationsDrawer />
      </AdvertisingApiClientProvider>
    );
  }
};

const AccountDropDownRenderer = ({
  accountDropdownType,
}): React.ReactElement | null => {
  if (accountDropdownType === ACCOUNT_DROPDOWN_TYPE.EMPTY) {
    return <EmptyAccountDropdown />;
  }

  if (accountDropdownType === ACCOUNT_DROPDOWN_TYPE.STANDARD) {
    return (
      <AccountDropdownProvider>
        <AccountDropdown />
      </AccountDropdownProvider>
    );
  }

  return null;
};

export const HelpDropdownRenderer = (): React.ReactElement => {
  return (
    <AccountDropdownProvider>
      <HelpDropdown />
    </AccountDropdownProvider>
  );
};

const UtilityMenu: React.VFC<{
  shouldShowHelpDropdown: boolean;
  shouldShowUserDropdown: boolean;
  communicationDrawerInfo: CommunicationsDrawerInfo | undefined;
  shouldShowSpooferInfo: boolean;
  isBackUpNav: boolean;
  signOutInfo: SignOutInfo;
  isMobile: boolean;
  selectedToolbarItem: SelectedToolbarItem | undefined;
  isSpoofing: boolean;
  currentAccountInfo: CurrentAccountInfo | undefined;
}> = ({
  shouldShowHelpDropdown,
  shouldShowUserDropdown,
  communicationDrawerInfo,
  shouldShowSpooferInfo,
  isBackUpNav,
  signOutInfo,
  isMobile,
  selectedToolbarItem,
  isSpoofing,
  currentAccountInfo,
}) => {
  const chromeConfig = useChromeConfigContext();
  const pageLayoutType = chromeConfig.pageLayoutType;
  const isStandardPage = isStandardPageLayoutType(pageLayoutType);
  const { config: internalChromeConfig } = useInternalChromeConfigContext();
  const bundle = useHeaderBundle();
  const { isInternalView, isInterstitialPage } = internalChromeConfig;

  const shouldShowCommunicationsDrawer =
    communicationDrawerInfo !== undefined &&
    !isBackUpNav &&
    isStandardPage &&
    !isInterstitialPage;

  const shouldShowAccountDropdown = !isInterstitialPage;

  const disableExternalToggle = (
    selectedToolbarItem
      ? !selectedToolbarItem.shouldDisplayExternalToggle
      : isInternalView && pageLayoutType === PAGE_LAYOUT_TYPE.INTERNAL_STANDARD
  ) as boolean;

  let accountDropdownType = ACCOUNT_DROPDOWN_TYPE.NONE;
  if (!isMobile && !isSpoofing && currentAccountInfo && isStandardPage) {
    accountDropdownType = ACCOUNT_DROPDOWN_TYPE.STANDARD;
  } else if (!isMobile && isSpoofing && currentAccountInfo && isStandardPage) {
    accountDropdownType = ACCOUNT_DROPDOWN_TYPE.EMPTY;
  }

  const isAdsNavigatorEnabled = useIsFeatureEnabled(
    WeblabedFeatures.ADS_NAVIGATOR_P0,
  );

  const isFromBTSWOrTrim = isBtswContext() || isFromBtswWc() || isFromTrim();

  const UtilitiesIconButtonWithAriaLabel = (props) => (
    <UtilityButton
      {...props}
      icon={questionCircleOutline}
      label={bundle.getMessage('open-the-help-panel')}
      {...getTaktAttributes('hmh-trigger', chromeComponents.HEADER)}
    />
  );

  return (
    <UtilityNavContainer>
      {!isMobile && isInternalView && (
        <ExternalSwitchWrapper>
          <ExternalToggle isDisabled={disableExternalToggle} />
        </ExternalSwitchWrapper>
      )}
      {isAdsNavigatorEnabled && !isMobile && <SearchBar />}
      {shouldShowAccountDropdown && (
        <AccountDropDownRenderer accountDropdownType={accountDropdownType} />
      )}
      {shouldShowCommunicationsDrawer && renderCommunicationDrawer(isMobile)}
      {!isMobile && isFromBTSWOrTrim ? (
        <HMHConsistentIngressAnchor
          // pass the styled components as props to ensure if styles change in this package
          // it does not require an update in HMHClient package
          UtilitiesIconButton={UtilitiesIconButtonWithAriaLabel}
          applicationName={internalChromeConfig.applicationName}
        />
      ) : shouldShowHelpDropdown ? (
        <HelpDropdownRenderer />
      ) : null}
      <ConnectedSalesAssistantButton />
      {shouldShowUserDropdown && (
        <SidenavBundleProvider>
          <UserDropdown />
        </SidenavBundleProvider>
      )}
      {shouldShowSpooferInfo && <SpoofingInfo />}
      {isBackUpNav && <SignOutLink signOutInfo={signOutInfo} inverted />}
    </UtilityNavContainer>
  );
};

const connector = connect((state: RootState) => {
  const currentAccountInfo = selectHeaderState(state).currentAccountInfo;
  const viewType = selectViewType(state);
  const isSpoofing = selectIsSpoofing(state);
  const communicationDrawerInfo = selectCommunicationsDrawerInfo(state);
  const isBackUpNav = selectIsBackUpNav(state);
  const signOutInfo = selectSignOutInfo(state);
  const isMobile = viewType === VIEW_TYPE.MOBILE;
  const shouldShowHelpDropdown = selectHelpMenu(state)?.length > 0;
  const shouldShowUserDropdown =
    viewType === VIEW_TYPE.DESKTOP && !isSpoofing && !isBackUpNav;

  const shouldShowSpooferInfo =
    !isMobile && isSpoofing && viewType === VIEW_TYPE.DESKTOP;
  const selectedToolbarItem = selectSelectedToolbarItem(state);

  return {
    shouldShowHelpDropdown,
    shouldShowUserDropdown,
    communicationDrawerInfo,
    shouldShowSpooferInfo,
    isBackUpNav,
    signOutInfo,
    isMobile,
    selectedToolbarItem,
    isSpoofing,
    currentAccountInfo,
  };
});

export const ConnectedUtilityMenu = connector(UtilityMenu);
